import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Snackbar,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { CalendarClock, Save } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import { useAuth } from "../../contexts/AuthContext";

const YEAR_FORMAT = /^(\d{4})-(\d{4})$/;

const isValidAcademicYear = (value) => {
	const match = YEAR_FORMAT.exec(value || "");
	if (!match) return false;
	const start = parseInt(match[1], 10);
	const end = parseInt(match[2], 10);
	return end === start + 1;
};

const AcademicYearSettings = () => {
	const { currentAcademicYear, refreshAcademicYear, setAcademicYearOptimistic } =
		useAcademicYear();
	const { user } = useAuth();

	const [newYear, setNewYear] = useState("");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const isValid = isValidAcademicYear(newYear);
	const isSameAsCurrent = newYear === currentAcademicYear;

	const handleSave = async () => {
		if (!isValid || isSameAsCurrent) return;

		setSaving(true);
		try {
			const { error } = await supabase
				.from("app_settings")
				.upsert(
					{
						key: "current_academic_year",
						value: newYear,
						updated_at: new Date().toISOString(),
						updated_by: user?.id || null,
					},
					{ onConflict: "key" }
				);

			if (error) throw error;

			setAcademicYearOptimistic(newYear);
			await refreshAcademicYear({ silent: true });

			setSnackbar({
				open: true,
				message: `Academic year is now ${newYear}. Teacher dashboards will reset on next refresh.`,
				severity: "success",
			});
			setNewYear("");
		} catch (err) {
			console.error("Failed to update academic year:", err);
			setSnackbar({
				open: true,
				message: err.message || "Failed to update academic year",
				severity: "error",
			});
		} finally {
			setSaving(false);
			setConfirmOpen(false);
		}
	};

	return (
		<Container maxWidth="md" sx={{ py: 4 }}>
			<Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
				<CalendarClock size={28} />
				<Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
					Academic Year Settings
				</Typography>
			</Stack>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
				Roll the system over to a new academic year. All teacher-facing pages
				(course progress, video uploads, in-course quizzes) will start fresh.
				Historical data for prior years remains in the database and is
				accessible to admins via the year selector on each report.
			</Typography>

			<Paper sx={{ p: 4, borderRadius: 2 }}>
				<Stack spacing={3}>
					<Box>
						<Typography variant="subtitle2" color="text.secondary">
							Current academic year
						</Typography>
						<Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
							{currentAcademicYear}
						</Typography>
					</Box>

					<TextField
						label="New academic year"
						placeholder="e.g. 2026-2027"
						value={newYear}
						onChange={(e) => setNewYear(e.target.value.trim())}
						helperText="Format: YYYY-YYYY where the second year is the first plus one (e.g. 2026-2027)."
						error={newYear.length > 0 && !isValid}
						fullWidth
					/>

					{isValid && isSameAsCurrent && (
						<Alert severity="info">
							This is already the current academic year.
						</Alert>
					)}

					<Alert severity="warning">
						<strong>Heads up:</strong> Once you switch the year, teachers will
						see empty progress and no uploads until they start working in the
						new year. This change is reversible (just switch back), but
						confusing for users mid-day.
					</Alert>

					<Box>
						<Button
							variant="contained"
							color="primary"
							size="large"
							startIcon={<Save size={18} />}
							onClick={() => setConfirmOpen(true)}
							disabled={!isValid || isSameAsCurrent || saving}>
							{saving ? "Saving..." : "Update Academic Year"}
						</Button>
					</Box>
				</Stack>
			</Paper>

			<Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)}>
				<DialogTitle>Confirm academic year rollover</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to switch the academic year from{" "}
						<strong>{currentAcademicYear}</strong> to{" "}
						<strong>{newYear}</strong>?
						<br />
						<br />
						All teacher dashboards will reset to a clean state. Existing
						{" " + currentAcademicYear + " "}
						data is preserved and remains visible to admins via the year
						selector on each report.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmOpen(false)} disabled={saving}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						color="primary"
						disabled={saving}
						startIcon={saving ? <CircularProgress size={16} /> : null}>
						{saving ? "Saving..." : "Yes, roll over"}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default AcademicYearSettings;
